import { useEffect, useState } from "react";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Lasso, AlertTriangle, RotateCcw, Undo2, BookOpen, X, Dna } from "lucide-react";
import { MarkingLayers } from "@/components/game/MarkingLayers";

const familyTint = (fam) => ({ Chestnut: "#9c5a2c", Bay: "#6b4423", Black: "#2b2622" }[fam] || "#7a5230");
const sexWord = (s) => (s === "stallion" ? "Stallion" : s === "mare" ? "Mare" : s);
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (p) => (p ? (p.startsWith("http") ? p : `${BACKEND}${p}`) : null);

const ColorChip = ({ phenotype, size = 40, artImage, missing, overlays = [] }) => (
  artImage ? (
    <div className="relative shrink-0 overflow-hidden rounded-lg" style={{ width: size, height: size, background: "#f5efe6" }}>
      <img src={imgUrl(artImage)} alt={phenotype?.base_color || "coat"} className="absolute inset-0 h-full w-full object-contain" style={{ zIndex: 1 }} />
      <MarkingLayers base={artImage} layers={overlays} compact={size < 96} />
    </div>
  ) : missing ? (
    <span className="grid shrink-0 place-items-center rounded-lg border border-dashed border-stone-300 bg-stone-100 text-center text-[8px] font-bold leading-tight text-stone-500"
      style={{ width: size, height: size }} title="Artwork not available yet">
      No art yet
    </span>
  ) : (
    <span className="grid shrink-0 place-items-center rounded-lg text-[10px] font-bold text-white/90"
      style={{ width: size, height: size, background: familyTint(phenotype?.base_family) }}
      title={phenotype?.base_color}>
      {(phenotype?.base_color || "?").slice(0, 2)}
    </span>
  )
);

const GenoLine = ({ genetics }) => {
  const labels = { extension: "E", agouti: "A", cream: "Cr", dun: "D", gray: "G", toro: "TO/RN", overo: "O", lp: "LP" };
  return (
    <div className="grid grid-cols-4 gap-x-2 gap-y-0.5 font-mono text-[10px] text-muted-foreground">
      {Object.keys(labels).map((k) => genetics?.[k] ? <span key={k}>{labels[k]}:{genetics[k]}</span> : null)}
    </div>
  );
};

// ---------------- Roundups ----------------
export const RoundupTab = ({ onChanged }) => {
  const [st, setSt] = useState(null);
  const [total, setTotal] = useState(10);
  const [malePct, setMalePct] = useState(50);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [history, setHistory] = useState(null);

  const load = async () => {
    const { data } = await gameApi.get("/game/hma/roundup");
    setSt(data);
    if (data.draft) setTotal(data.draft.requested_total);
  };
  useEffect(() => { load(); gameApi.get("/game/hma/roundups").then(({ data }) => setHistory(data.roundups)).catch(() => setHistory([])); }, []);

  const start = async () => {
    setBusy(true);
    try { await gameApi.post("/game/hma/roundup/start", { total: Number(total), male_pct: Number(malePct) }); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const toggleRelease = async (id, released) => {
    try { const { data } = await gameApi.post("/game/hma/roundup/release", { horse_id: id, released }); setSt((p) => ({ ...p, draft: data.draft })); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const cancel = async () => { await gameApi.post("/game/hma/roundup/cancel"); await load(); };
  const finalize = async () => {
    setBusy(true);
    try {
      const { data } = await gameApi.post("/game/hma/roundup/finalize");
      toast.success(`Roundup finalized — ${data.finalized_count} gathered, ${data.released_count} released`);
      setConfirming(false);
      await load();
      onChanged?.();
      gameApi.get("/game/hma/roundups").then(({ data: d }) => setHistory(d.roundups));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  if (!st) return <div className="flex items-center gap-2 p-6 text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Loading…</div>;
  const d = st.draft;

  return (
    <div className="space-y-3" data-testid="roundup-tab">
      <div className="rounded-xl border border-border bg-card p-3 text-xs">
        <p className="flex items-center gap-1 font-bold text-primary"><Lasso size={15} /> Roundups · Year {st.current_year}</p>
        <p className="mt-1 text-muted-foreground">Population {st.population} / capacity {st.carrying_capacity} · {st.males_available} stallions · {st.females_available} mares{st.last_roundup_year ? ` · last roundup Y${st.last_roundup_year}` : ""}</p>
      </div>
      <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-[11px] text-amber-800" data-testid="roundup-reminder">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <span><b>Roundups are optional.</b> Releasing horses removes them for good and can wipe out a rare gene or a scarce sex from your herd — watch for the ⚠️ rare-trait flags before finalizing. Gather only when your herd truly needs it{st.males_available <= 2 ? ` · only ${st.males_available} stallion(s) left` : ""}{st.females_available <= 2 ? ` · only ${st.females_available} mare(s) left` : ""}.</span>
      </div>

      {!d && !st.available && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800" data-testid="roundup-unavailable">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" /> <span>{st.reason}</span>
        </div>
      )}

      {!d && st.available && (
        <div className="space-y-3 rounded-xl border border-border p-3" data-testid="roundup-setup">
          <div>
            <label className="text-xs font-bold">Horses to gather: {total}</label>
            <input data-testid="roundup-total" type="range" min={1} max={st.population} value={total} onChange={(e) => setTotal(e.target.value)} className="w-full" />
          </div>
          <div>
            <label className="text-xs font-bold">Sex split: {malePct}% stallions / {100 - malePct}% mares</label>
            <input data-testid="roundup-malepct" type="range" min={0} max={100} step={5} value={malePct} onChange={(e) => setMalePct(e.target.value)} className="w-full" />
          </div>
          <button data-testid="roundup-start-btn" disabled={busy} onClick={start} className="w-full rounded-lg bg-primary py-2 text-sm font-bold text-primary-foreground disabled:opacity-50">{busy ? "Gathering…" : "Generate Roundup"}</button>
        </div>
      )}

      {d && (
        <div className="space-y-3" data-testid="roundup-sheet">
          <div className="rounded-xl border border-border bg-card p-3 text-xs">
            <p className="font-bold text-primary">Roundup Sheet</p>
            <p className="text-muted-foreground">Requested {d.requested_total} · released {d.released_count} · <span className="font-bold text-foreground">final {d.final_count}</span> ({d.final_males}♂ / {d.final_females}♀)</p>
            <p className="text-muted-foreground">Population {st.population} → <span className="font-bold text-foreground">{st.population - d.final_count}</span> after gather · capacity {st.carrying_capacity}</p>
          </div>
          <div className="space-y-1.5">
            {d.horses.map((h) => (
              <div key={h.id} data-testid={`roundup-horse-${h.hma_number}`} className={`rounded-lg border p-2 text-xs ${h.released ? "border-dashed border-muted-foreground/40 opacity-60" : "border-border bg-card"}`}>
                <div className="flex items-center gap-2">
                  <ColorChip phenotype={h.phenotype} artImage={h.art_image} overlays={h.overlays} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-bold">{h.label} · {h.phenotype?.base_color}</p>
                    <p className="text-[11px] text-muted-foreground">{sexWord(h.sex)} · age {h.age} · {h.band_status}{h.band_stallion ? ` (band: ${h.band_stallion})` : ""} · {h.body_model}</p>
                  </div>
                  <button data-testid={`roundup-release-${h.hma_number}`} onClick={() => toggleRelease(h.id, !h.released)} className={`flex items-center gap-1 rounded px-2 py-1 text-[11px] font-bold ${h.released ? "bg-emerald-100 text-emerald-700" : "border border-border"}`}>
                    {h.released ? <><Undo2 size={12} /> Released</> : <><RotateCcw size={12} /> Release</>}
                  </button>
                </div>
                <div className="mt-1 pl-12">
                  <p className="text-[10px] text-muted-foreground">Sire: {h.sire} · Dam: {h.dam}</p>
                  <GenoLine genetics={h.genetics} />
                  {h.rare_flags.map((f) => (
                    <p key={f.allele} data-testid={`roundup-rare-${h.hma_number}-${f.allele}`} className="mt-0.5 flex items-center gap-1 text-[10px] font-bold text-rose-600"><AlertTriangle size={11} /> Rare Genetics ({f.trait}) — releasing may help preserve this trait.</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button data-testid="roundup-cancel-btn" onClick={cancel} className="flex-1 rounded-lg border border-border py-2 text-sm font-bold">Cancel Roundup</button>
            <button data-testid="roundup-finalize-btn" disabled={d.final_count === 0} onClick={() => setConfirming(true)} className="flex-1 rounded-lg bg-primary py-2 text-sm font-bold text-primary-foreground disabled:opacity-50">Finalize ({d.final_count})</button>
          </div>
        </div>
      )}

      {history && history.length > 0 && (
        <details className="rounded-xl border border-border p-2 text-xs" data-testid="roundup-history">
          <summary className="cursor-pointer font-bold text-primary">Past Roundups ({history.length})</summary>
          <div className="mt-2 space-y-1">
            {history.map((r) => <p key={r.id} className="text-muted-foreground">Year {r.game_year}: gathered {r.finalized_count} ({r.final_males}♂/{r.final_females}♀), released {r.released_count} of {r.initial_count} requested</p>)}
          </div>
        </details>
      )}

      {confirming && d && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4" onClick={() => setConfirming(false)}>
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-4 text-sm" onClick={(e) => e.stopPropagation()} data-testid="roundup-confirm">
            <p className="font-display text-lg font-bold text-primary">Finalize Roundup?</p>
            <p className="mt-1 text-xs text-muted-foreground">Gathering <span className="font-bold text-foreground">{d.final_count}</span> horses ({d.final_males}♂ / {d.final_females}♀). They leave the wild herd permanently and enter the shared makeover holding pool. This cannot be undone.</p>
            <p className="mt-1 text-xs text-muted-foreground">Population {st.population} → {st.population - d.final_count} · capacity {st.carrying_capacity}</p>
            <div className="mt-3 flex gap-2">
              <button onClick={() => setConfirming(false)} className="flex-1 rounded-lg border border-border py-2 text-xs font-bold">Back</button>
              <button data-testid="roundup-confirm-btn" disabled={busy} onClick={finalize} className="flex-1 rounded-lg bg-primary py-2 text-xs font-bold text-primary-foreground disabled:opacity-50">{busy ? "Finalizing…" : "Confirm & Finalize"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------- Gather Book ----------------
const STATUS_FILTERS = [["all", "All"], ["In Holding", "In Holding"], ["In Makeover", "In Makeover"], ["completed", "Completed"], ["Kept", "Kept"], ["Sold", "Sold"]];

export const GatherBookTab = () => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("all");
  const [sex, setSex] = useState("all");
  const [year, setYear] = useState(0);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);

  const load = async () => {
    const { data } = await gameApi.get(`/game/hma/gather-book?status=${encodeURIComponent(status)}&sex=${sex}&year=${year}&q=${encodeURIComponent(q)}`);
    setData(data);
  };
  useEffect(() => { load(); }, [status, sex, year]); // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  const open = async (id) => { const { data } = await gameApi.get(`/game/hma/gather-book/${id}`); setSel(data.entry); };

  if (!data) return <div className="flex items-center gap-2 p-6 text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Loading…</div>;
  const s = data.stats;

  return (
    <div className="space-y-3" data-testid="gather-book-tab">
      <div className="grid grid-cols-3 gap-1.5 text-center text-xs sm:grid-cols-6" data-testid="gather-stats">
        {[["Total", s.total], ["Holding", s.in_holding], ["Makeover", s.in_makeover], ["Kept", s.kept], ["Sold", s.sold], ["Best Score", s.highest_score ?? "—"]].map(([l, v]) => (
          <div key={l} className="rounded-lg border border-border bg-card py-1.5"><p className="text-base font-bold text-primary">{v}</p><p className="text-[10px] text-muted-foreground">{l}</p></div>
        ))}
      </div>
      {(s.best_placing || s.highest_sale) ? (
        <p className="text-[11px] text-muted-foreground">{s.best_placing ? `Best placing: ${s.best_placing}` : ""}{s.best_placing && s.highest_sale ? " · " : ""}{s.highest_sale ? `Highest sale: ${s.highest_sale}🪙` : ""}</p>
      ) : null}

      <input data-testid="gather-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search HMA #, tag, or name…" className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-xs" />
      <div className="flex flex-wrap gap-1">
        {STATUS_FILTERS.map(([k, l]) => <button key={k} data-testid={`gather-filter-${k}`} onClick={() => setStatus(k)} className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${status === k ? "bg-primary text-primary-foreground" : "border border-border"}`}>{l}</button>)}
        {["all", "male", "female"].map((k) => <button key={k} onClick={() => setSex(k)} className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${sex === k ? "bg-secondary text-secondary-foreground" : "border border-border"}`}>{k === "all" ? "Any sex" : k === "male" ? "♂" : "♀"}</button>)}
        {data.years.length > 0 && <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="rounded-full border border-border bg-background px-2 py-1 text-[11px] font-bold"><option value={0}>Any year</option>{data.years.map((y) => <option key={y} value={y}>Y{y}</option>)}</select>}
      </div>

      {data.entries.length === 0 ? (
        <p className="p-4 text-center text-xs text-muted-foreground">No gathered horses match. Finalize a roundup to add horses to your Gather Book.</p>
      ) : (
        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {data.entries.map((e) => (
            <button key={e.id} data-testid={`gather-entry-${e.makeover_tag}`} onClick={() => open(e.id)} className="flex items-center gap-2 rounded-lg border border-border bg-card p-2 text-left text-xs">
              <ColorChip phenotype={e.phenotype} artImage={e.art_image} missing={e.art_missing} overlays={e.overlays} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-bold">#{e.makeover_tag}{e.makeover_name ? ` · ${e.makeover_name}` : e.wild_nickname ? ` · ${e.wild_nickname}` : ""}</p>
                <p className="truncate text-[11px] text-muted-foreground">{e.phenotype?.base_color} · {sexWord(e.sex)} · HMA #{e.hma_number}</p>
                <p className="text-[10px] font-bold text-secondary">{e.status_display}{e.status === "In Holding" && e.makeover_eligible === false ? " · Eligible at age 3" : ""}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {sel && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4" onClick={() => setSel(null)}>
          <div className="max-h-[85vh] w-full max-w-sm overflow-y-auto rounded-2xl border border-border bg-card p-4 text-sm" onClick={(e) => e.stopPropagation()} data-testid="gather-detail">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display text-lg font-bold text-primary">#{sel.makeover_tag}{sel.makeover_name ? ` · ${sel.makeover_name}` : ""}</h3>
                <p className="text-xs text-muted-foreground">{sel.wild_nickname ? `Wild: ${sel.wild_nickname} · ` : ""}HMA #{sel.hma_number} · {sel.hma_name}</p>
              </div>
              <button onClick={() => setSel(null)}><X size={18} /></button>
            </div>
            <div className="mt-2 flex items-center gap-3">
              <ColorChip phenotype={sel.phenotype} size={64} artImage={sel.art_image} missing={sel.art_missing} overlays={sel.resolved_layers} />
              <div className="text-xs">
                <p className="font-bold text-primary">{sel.phenotype?.base_color}</p>
                <p className="text-muted-foreground">{sexWord(sel.sex)} · age at gather {sel.age_at_gather}{sel.current_age != null ? ` · now age ${sel.current_age}` : ""} · gathered Y{sel.gather_game_year}</p>
                <p className="text-[11px] font-bold text-secondary">{sel.status_display}</p>
                {sel.status === "In Holding" && sel.makeover_eligible === false && (
                  <p className="text-[11px] font-bold text-amber-600" data-testid="gather-eligible-note">In holding — eligible for makeover pool at age 3</p>
                )}
              </div>
            </div>
            <div className="mt-2 rounded-lg bg-muted/40 p-2">
              <p className="flex items-center gap-1 text-xs font-bold"><Dna size={13} /> Genetics · {sel.body_model}</p>
              <GenoLine genetics={sel.genetics} />
              <p className="mt-1 text-[11px] text-muted-foreground">Sire: {sel.sire} · Dam: {sel.dam}</p>
            </div>
            {(sel.makeover_player_name || sel.makeover_score != null) && (
              <div className="mt-2 rounded-lg border border-border p-2 text-xs" data-testid="gather-outcome">
                <p className="font-bold">Makeover Outcome</p>
                {sel.makeover_player_name && <p className="text-muted-foreground">Trainer: {sel.makeover_player_name}</p>}
                {sel.makeover_score != null && <p className="text-muted-foreground">Score: {sel.makeover_score} · Placing: {sel.makeover_placing}</p>}
                {sel.sale_price ? <p className="text-muted-foreground">Sold for {sel.sale_price}🪙</p> : null}
              </div>
            )}
            {sel.art_missing && <p className="mt-2 text-[10px] text-amber-600" data-testid="gather-art-missing">Base artwork for {sel.art_phenotype} · {sel.body_model} not available yet — genetics shown are exact.</p>}
            {sel.missing_assets?.length > 0 && <p className="mt-1 text-[10px] text-amber-600">Some marking overlays pending upload.</p>}
          </div>
        </div>
      )}
    </div>
  );
};
